import { useEffect, useRef, useState } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Close';

/**
 * Search
 * Classification: custom (composes MUI TextField + adornments)
 *
 * A controlled/uncontrolled search input with a leading search icon, a clear
 * button, and debounced change notifications - a standard product pattern not
 * provided directly by MUI.
 */
export interface SearchProps
  extends Omit<TextFieldProps, 'onChange' | 'value' | 'defaultValue'> {
  value?: string;
  defaultValue?: string;
  /** Debounced value change (ms set by `debounceMs`). */
  onSearch?: (value: string) => void;
  /** Immediate change on each keystroke. */
  onChange?: (value: string) => void;
  debounceMs?: number;
}

export function Search({
  value,
  defaultValue = '',
  onSearch,
  onChange,
  debounceMs = 300,
  placeholder = 'Search...',
  ...rest
}: SearchProps) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);
  const current = isControlled ? value : internal;
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const update = (next: string) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch?.(next), debounceMs);
  };

  return (
    <TextField
      {...rest}
      value={current}
      placeholder={placeholder}
      onChange={(e) => update(e.target.value)}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: current ? (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear search"
                size="small"
                edge="end"
                onClick={() => update('')}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
}
